import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Search as SearchIcon, Film, Tv as TvIcon, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function TmdbChannelArtworkModal({
  open, initialQuery = "", targetId, apiBase, authHeaders, onClose, onApplied,
}) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [selected, setSelected] = useState({ thumbnail: "", logo: "", guide_poster: "" });
  const [loading, setLoading] = useState(false);
  const [artLoading, setArtLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ(initialQuery || "");
    setResults([]);
    setPicked(null);
    setArtwork(null);
    setSelected({ thumbnail: "", logo: "", guide_poster: "" });
    setStatus(null);
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setStatus(null);
      try {
        const params = new URLSearchParams({ q: q.trim() });
        const res = await fetch(`${apiBase}/tmdb/search?${params}`, { headers: authHeaders() });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Search failed (${res.status})`);
        }
        const data = await res.json();
        setResults(data.results || []);
        if (!(data.results || []).length) setStatus({ level: "info", text: "No matches. Try another title." });
      } catch (e) {
        setStatus({ level: "error", text: e.message || "TMDb search failed." });
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, apiBase, authHeaders]);

  const chooseIdentity = async (entry) => {
    setPicked(entry);
    setArtwork(null);
    setSelected({ thumbnail: "", logo: "", guide_poster: "" });
    setArtLoading(true);
    setStatus(null);
    try {
      const params = new URLSearchParams({
        media_type: entry.media_type,
        tmdb_id: String(entry.tmdb_id),
      });
      const res = await fetch(`${apiBase}/tmdb/channel-artwork-preview?${params}`, { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Artwork lookup failed (${res.status})`);
      }
      const data = await res.json();
      setArtwork(data);
      setSelected({
        thumbnail: data.recommended?.thumbnail || "",
        logo: data.recommended?.logo || "",
        guide_poster: data.recommended?.guide_poster || "",
      });
    } catch (e) {
      setStatus({ level: "error", text: e.message || "Artwork lookup failed." });
    } finally {
      setArtLoading(false);
    }
  };

  const useRecommended = () => {
    if (!artwork) return;
    setSelected({
      thumbnail: artwork.recommended?.thumbnail || "",
      logo: artwork.recommended?.logo || "",
      guide_poster: artwork.recommended?.guide_poster || "",
    });
  };

  const applyArtwork = async () => {
    if (!picked || !targetId || !Object.values(selected).some(Boolean)) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/tmdb/channel-artwork/${targetId}`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          tmdb_id: picked.tmdb_id,
          media_type: picked.media_type,
          thumbnail_url: selected.thumbnail || null,
          logo_url: selected.logo || null,
          guide_poster_url: selected.guide_poster || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Apply failed (${res.status})`);
      }
      const updated = await res.json();
      toast.success("TMDb channel artwork applied", {
        description: `${picked.title}${picked.year ? ` (${picked.year})` : ""}`,
      });
      if (onApplied) await onApplied(updated);
      onClose();
    } catch (e) {
      toast.error("Artwork apply failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const identityLabel = useMemo(() => !picked ? "" :
    `${picked.title}${picked.year ? ` (${picked.year})` : ""} · ${picked.media_type.toUpperCase()} ${picked.tmdb_id}`,
  [picked]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
      data-testid="tmdb-channel-artwork-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-6xl max-h-[92vh] flex flex-col bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)]"
        style={{ fontFamily: "JetBrains Mono, monospace" }}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)]">
          <div className="text-[14px] tracking-[0.22em]" style={{ fontFamily: "VT323, monospace" }}>
            🎬 TMDB CHANNEL ARTWORK
          </div>
          <button onClick={onClose} className="text-[11px] border border-[var(--phosphor)] px-2 py-0.5 hover:bg-[var(--phosphor)] hover:text-black flex items-center gap-1">
            <X size={11} /> ESC
          </button>
        </div>

        <div className="flex items-center gap-2 p-3 border-b border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]">
          <SearchIcon size={14} className="opacity-70" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search the exact TV series or movie"
            className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[13px] outline-none" />
          {loading && <span className="text-[10px] opacity-70">SEARCHING…</span>}
        </div>

        {status && <div className={`px-3 py-1.5 text-[11px] ${status.level === "error" ? "text-[#FF6B6B]" : "opacity-80"}`}>{status.text}</div>}

        <div className="flex-1 overflow-y-auto">
          {!picked && results.length > 0 && (
            <div className="p-3">
              <div className="text-[10px] tracking-[0.2em] opacity-70 mb-2">&gt; SELECT THE CORRECT TMDB IDENTITY</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {results.map((r) => (
                  <button key={`${r.media_type}-${r.tmdb_id}`} onClick={() => chooseIdentity(r)}
                    className="text-left border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] hover:border-[var(--phosphor)]">
                    {r.poster_thumb_url ? <img src={r.poster_thumb_url} alt={r.title} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                      : <div className="w-full aspect-[2/3] flex items-center justify-center text-[9px] opacity-50">NO POSTER</div>}
                    <div className="p-1.5 text-[11px]">
                      <div className="flex items-center gap-1 opacity-60 text-[9px]">
                        {r.media_type === "tv" ? <TvIcon size={10} /> : <Film size={10} />}
                        {r.media_type.toUpperCase()}{r.year ? ` · ${r.year}` : ""}
                      </div>
                      <div className="truncate">{r.title}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {picked && (
            <div className="p-3">
              <div className="flex items-center justify-between gap-3 border border-[var(--phosphor)] p-2 mb-3">
                <div>
                  <div className="text-[9px] opacity-60 tracking-[0.2em]">VERIFIED ARTWORK SOURCE</div>
                  <div className="text-[13px]">{identityLabel}</div>
                </div>
                <button onClick={() => { setPicked(null); setArtwork(null); }}
                  className="text-[10px] border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] px-2 py-1">
                  CHANGE SOURCE
                </button>
              </div>

              {artLoading && <div className="text-center py-10 text-[11px] opacity-70">LOADING TMDB ARTWORK…</div>}
              {artwork && !artLoading && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <ArtworkColumn title="CHANNEL THUMBNAIL" hint="Portrait TMDb poster · previewed as the site's 9:16 channel card" items={artwork.posters || []}
                      selected={selected.thumbnail} onSelect={(url) => setSelected((p) => ({ ...p, thumbnail: url }))} aspect="channel" />
                    <ArtworkColumn title="STATION LOGO" hint="Transparent TMDb title logo" items={artwork.logos || []}
                      selected={selected.logo} onSelect={(url) => setSelected((p) => ({ ...p, logo: url }))} aspect="logo" />
                    <ArtworkColumn title="GUIDE POSTER" hint="Landscape TMDb backdrop · previewed as the site's 4:3 guide panel" items={artwork.backdrops || []}
                      selected={selected.guide_poster} onSelect={(url) => setSelected((p) => ({ ...p, guide_poster: url }))} aspect="guide" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] flex flex-wrap justify-between gap-2">
                    <div className="text-[9px] opacity-60">Artwork only — episode identity and numbering stay untouched.</div>
                    <div className="flex gap-2">
                      <button onClick={useRecommended} className="text-[10px] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-3 py-1">
                        USE RECOMMENDED SET
                      </button>
                      <button onClick={applyArtwork} disabled={saving || !Object.values(selected).some(Boolean)}
                        className="text-[10px] border border-[var(--phosphor)] px-3 py-1 hover:bg-[var(--phosphor)] hover:text-black disabled:opacity-40 flex items-center gap-1">
                        <Sparkles size={11} /> {saving ? "APPLYING…" : "APPLY SELECTED ARTWORK"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-3 py-1.5 text-[9px] opacity-50 text-right border-t border-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]">
          metadata &amp; images ↝ themoviedb.org · this product uses the TMDb API but is not endorsed or certified by TMDb.
        </div>
      </div>
    </div>
  );
}

function ArtworkColumn({ title, hint, items, selected, onSelect, aspect }) {
  const aspectClass =
    aspect === "channel" ? "aspect-[9/16]" :
    aspect === "guide" ? "aspect-[4/3]" :
    aspect === "logo" ? "aspect-[16/7]" :
    "aspect-video";
  return (
    <div className="border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] p-2 min-w-0">
      <div className="text-[11px] tracking-[0.15em]">{title}</div>
      <div className="text-[9px] opacity-55 mb-2">{hint}</div>
      {!items.length ? <div className="h-28 flex items-center justify-center border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] text-[9px] opacity-50">NO TMDB ARTWORK AVAILABLE</div>
      : <div className="grid grid-cols-2 gap-2 max-h-[340px] overflow-y-auto pr-1">
        {items.map((item, idx) => {
          const active = selected === item.url;
          return <button key={`${item.url}-${idx}`} onClick={() => onSelect(item.url)}
            className={`relative border p-1 ${active ? "border-[var(--phosphor)]" : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]"}`}>
            <div className={`${aspectClass} bg-black flex items-center justify-center overflow-hidden`}>
              <img src={item.thumb_url || item.url} alt="" loading="lazy"
                className={aspect === "logo" ? "w-full h-full object-contain p-2" : "w-full h-full object-cover"} />
            </div>
            {active && <div className="absolute top-1 right-1 bg-black border border-[var(--phosphor)] p-0.5"><Check size={11} /></div>}
            <div className="text-[8px] opacity-60 mt-1 truncate">{idx === 0 ? "RECOMMENDED · " : ""}{item.width || "?"}×{item.height || "?"}</div>
          </button>;
        })}
      </div>}
    </div>
  );
}
